import React, { useState, useEffect, useCallback, useRef } from "react";
import { SmartMap } from "../../components/SmartMap";
import { MarkerInfoCard } from "./MarkerInfoCard";
import { useRoutePreview } from "./useRoutePreview";
import { api } from "../../lib/api";
import { ShieldAlert, Crosshair, X } from "lucide-react";

const RIDE_FUNNEL_STATES = ["searching", "offered", "accepted", "in_progress"];

const SOS_RED = "#DC2626";
const SOS_AMBER = "#F59E0B";
const sosColor = (level) => (level === "emergency" ? SOS_RED : SOS_AMBER);

/** Builds display-only SOS markers (last position) + breadcrumb polylines. */
function buildSosOverlay(sosEvents) {
  const markers = [];
  const routes = [];
  (sosEvents || []).forEach((s) => {
    const color = sosColor(s.level);
    if (s.last_lat != null && s.last_lng != null) {
      markers.push({
        id: `sos-${s.id}`, lat: s.last_lat, lng: s.last_lng, type: "sos", kind: "sos",
        label: `SOS · ${s.driver_name || "driver"}`, color, pulse: true,
      });
    }
    if (s.trail && s.trail.length >= 2) {
      routes.push({ points: s.trail, color, weight: 4, opacity: 0.85 });
    }
  });
  return { markers, routes };
}

/** Builds the marker list (drivers + active-ride pickup + dropoff pins). */
function buildMarkers(drivers, rides) {
  const driverMarkers = drivers
    .filter((d) => d.current_lat != null && d.current_lng != null)
    .map((d) => ({
      id: `drv-${d.id}`,
      lat: d.current_lat,
      lng: d.current_lng,
      type: "driver",
      label: d.vehicle_plate || d.name,
      // Offline pins go grey + no pulse; online pins stay yellow + pulse.
      color: d.is_online ? "#FFC107" : "#9CA3AF",
      pulse: !!d.is_online,
      kind: "driver",
      payload: d,
    }));
  const activeRideMarkers = rides
    .filter((r) => RIDE_FUNNEL_STATES.includes(r.status))
    .flatMap((r) => [
      {
        id: `pu-${r.id}`,
        lat: r.pickup_lat,
        lng: r.pickup_lng,
        type: "pickup",
        label: r.passenger_name,
        kind: "pickup",
        payload: r,
      },
      {
        id: `do-${r.id}`,
        lat: r.dropoff_lat,
        lng: r.dropoff_lng,
        type: "dropoff",
        label: r.dropoff_address?.split(",")[0],
        kind: "dropoff",
        payload: r,
      },
    ]);
  return { driverMarkers, activeRideMarkers, all: [...driverMarkers, ...activeRideMarkers] };
}

/**
 * Live operations console: full fleet, active-ride markers, clickable pins
 * with info card + optional pickup→dropoff route overlay (and driver→pickup
 * leg if assigned).
 *
 * Mounts only while the admin's Map tab is active.
 */
export const MapPanel = ({ drivers, rides }) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapHealth, setMapHealth] = useState("initialising");
  const [sosEvents, setSosEvents] = useState([]);
  const [followId, setFollowId] = useState(null);
  const [focus, setFocus] = useState(null);
  const nonceRef = useRef(0);
  const prevIdsRef = useRef(null);
  const { driverMarkers, all: allMarkers } = buildMarkers(drivers, rides);
  const routePreview = useRoutePreview(selectedMarker, drivers);
  const onlineCount = driverMarkers.filter((d) => d.payload?.is_online).length;
  const offlineCount = driverMarkers.filter((d) => !d.payload?.is_online).length;
  const activeRideCount = rides.filter((r) => RIDE_FUNNEL_STATES.includes(r.status)).length;

  // Read-only SOS overlay — poll active SOS (last position + breadcrumb trail).
  const fetchSos = useCallback(async () => {
    try { const { data } = await api.get("/admin/sos/live-map"); setSosEvents(data.events || []); }
    catch (e) { /* overlay best-effort */ }
  }, []);
  useEffect(() => { fetchSos(); const t = setInterval(fetchSos, 10000); return () => clearInterval(t); }, [fetchSos]);

  // Pan/zoom to a given SOS (display-only). setFollow keeps tracking it.
  const focusOn = useCallback((s, { setFollow = true, zoom } = {}) => {
    if (!s || s.last_lat == null || s.last_lng == null) return;
    nonceRef.current += 1;
    setFocus({ lat: s.last_lat, lng: s.last_lng, nonce: nonceRef.current, zoom });
    if (setFollow) setFollowId(s.id);
  }, []);
  const stopFollow = useCallback(() => setFollowId(null), []);

  // Auto-focus when a NEW SOS appears. First map load gently focuses any
  // existing emergency once (no continuous follow, so it won't snap back).
  useEffect(() => {
    const ids = sosEvents.map((e) => e.id);
    if (prevIdsRef.current === null) {
      prevIdsRef.current = ids;
      if (sosEvents.length) focusOn(sosEvents[0], { setFollow: false, zoom: 14 });
      return;
    }
    const newOnes = sosEvents.filter((e) => !prevIdsRef.current.includes(e.id));
    prevIdsRef.current = ids;
    if (newOnes.length) focusOn(newOnes[0], { setFollow: true, zoom: 15 });   // newest first
  }, [sosEvents, focusOn]);

  // While following, track the driver's movement (re-pan, keep current zoom).
  useEffect(() => {
    if (!followId) return;
    const s = sosEvents.find((e) => e.id === followId);
    if (!s || s.last_lat == null) return;
    nonceRef.current += 1;
    setFocus({ lat: s.last_lat, lng: s.last_lng, nonce: nonceRef.current });
  }, [sosEvents, followId]);

  // When all SOS clear, release focus/follow so normal fleet auto-fit resumes.
  useEffect(() => {
    if (sosEvents.length === 0) {
      setFocus(null); setFollowId(null); prevIdsRef.current = [];
    }
  }, [sosEvents.length]);

  const { markers: sosMarkers, routes: sosRoutes } = buildSosOverlay(sosEvents);
  const markers = [...allMarkers, ...sosMarkers];
  const routes = [...(routePreview?.routes || []), ...sosRoutes];

  return (
    <div
      className="relative"
      style={{ height: "calc(100vh - 200px)", minHeight: "480px" }}
      data-testid="admin-map-view"
    >
      <SmartMap
        markers={markers}
        routes={routes.length > 0 ? routes : null}
        className="absolute inset-0"
        showRoute={false}
        focusTarget={focus}
        onUserPan={stopFollow}
        disableAutoFit={!!focus}
        onMarkerClick={(m) => { if (m.kind === "sos") return; setSelectedMarker(m); }}
        onStatusChange={setMapHealth}
      />
      <div className="absolute top-3 left-3 right-3 md:right-auto bg-white/95 backdrop-blur-sm border border-border px-3 py-2 z-10 shadow-md">
        <div className="flex items-center gap-2 mb-0.5">
          <MapHealthDot status={mapHealth} />
          <div className="label-mini text-muted-foreground">FLEET STATUS</div>
        </div>
        <div className="font-mono-tab text-sm mt-0.5" data-testid="fleet-status-counts">
          <span className="font-bold text-[#10B981]">{onlineCount} online</span>
          <span className="text-muted-foreground mx-1.5">·</span>
          <span className="font-bold text-[#9CA3AF]">{offlineCount} offline</span>
          <span className="text-muted-foreground mx-1.5">·</span>
          <span className="font-bold">{activeRideCount} active rides</span>
        </div>
      </div>
      {sosEvents.length > 0 && (
        <div className="absolute top-3 right-3 z-20 bg-[#FEF2F2] border-2 border-[#DC2626] rounded-lg px-3 py-2 shadow-lg max-w-xs" data-testid="sos-map-banner">
          <div className="flex items-center justify-between gap-2 text-[#991B1B] font-bold text-sm">
            <span className="flex items-center gap-1.5"><ShieldAlert className="w-4 h-4 animate-pulse" /> {sosEvents.length} active SOS</span>
            {followId && (
              <button onClick={stopFollow} data-testid="sos-follow-stop"
                className="text-[10px] font-semibold bg-[#991B1B] text-white rounded px-1.5 py-0.5 flex items-center gap-1 hover:bg-[#7F1D1D]">
                <X className="w-3 h-3" /> Stop follow
              </button>
            )}
          </div>
          <div className="mt-1 space-y-1">
            {sosEvents.map((s) => {
              const following = followId === s.id;
              return (
                <div key={s.id} className="text-[11px] flex items-center justify-between gap-2" data-testid={`sos-map-row-${s.id}`}>
                  <span className="flex items-center gap-1 min-w-0">
                    <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: sosColor(s.level) }} />
                    <span className="font-semibold truncate">{s.driver_name || "driver"}</span>
                    <span className="text-muted-foreground flex-shrink-0">· {s.state}</span>
                  </span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => (following ? stopFollow() : focusOn(s, { setFollow: true, zoom: 16 }))}
                      data-testid={`sos-follow-${s.id}`}
                      className={`flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold ${following ? "bg-[#DC2626] text-white" : "border border-[#DC2626] text-[#991B1B] hover:bg-[#FEE2E2]"}`}>
                      <Crosshair className="w-3 h-3" /> {following ? "Following" : "Follow"}
                    </button>
                    {s.last_lat != null && (
                      <a href={`https://www.google.com/maps?q=${s.last_lat},${s.last_lng}`} target="_blank" rel="noreferrer"
                        className="text-[#6D28D9] hover:underline" data-testid={`sos-map-link-${s.id}`}>Maps ↗</a>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {selectedMarker && (
        <MarkerInfoCard
          marker={selectedMarker}
          drivers={drivers}
          routePreview={routePreview}
          onClose={() => setSelectedMarker(null)}
        />
      )}
    </div>
  );
};

/** Glanceable dot showing the map's runtime health.
 *  green  = tiles loaded · amber = initialising / fallback · red = failed */
const MAP_HEALTH = {
  loaded:       { color: "#10B981", title: "Map healthy — tiles loaded",        pulse: false },
  initialising: { color: "#F59E0B", title: "Map initialising…",                  pulse: true  },
  fallback:     { color: "#F59E0B", title: "Map fallback — no Google Maps key",  pulse: false },
  failed:       { color: "#EF4444", title: "Map failed to load — using fallback", pulse: true  },
};
const MapHealthDot = ({ status }) => {
  const s = MAP_HEALTH[status] || MAP_HEALTH.initialising;
  return (
    <span
      className={`w-2 h-2 rounded-full inline-block ${s.pulse ? "pulse-warning" : ""}`}
      style={{ backgroundColor: s.color }}
      title={s.title}
      data-testid="map-health-indicator"
      data-status={status}
    />
  );
};
